import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, CheckCircle2, Clock, Ban, DollarSign, ArrowUpRight,
  Users as UsersIcon, TrendingUp, Receipt, CreditCard,
} from "lucide-react";
import { api } from "../../lib/auth";

const toneMap = {
  emerald: { bg: "bg-[#E8F5E9]", txt: "text-[#1FA868]", ring: "ring-[#1FA868]/20" },
  amber:   { bg: "bg-[#FFF5D6]", txt: "text-amber-700",   ring: "ring-amber-500/20" },
  rose:    { bg: "bg-[#FDE7EA]", txt: "text-rose-600",    ring: "ring-rose-500/20" },
  slate:   { bg: "bg-[#EEF0F3]", txt: "text-slate-700",   ring: "ring-slate-500/20" },
  violet:  { bg: "bg-[#EEEBFA]", txt: "text-violet-700",  ring: "ring-violet-500/20" },
  sky:     { bg: "bg-[#E0F2FE]", txt: "text-sky-700",     ring: "ring-sky-500/20" },
};

function StatCard({ lbl, val, tone = "emerald", icon: Icon, delta, to }) {
  const t = toneMap[tone];
  const Wrapper = to ? Link : "div";
  return (
    <Wrapper {...(to ? { to } : {})}
      className="group bg-white border border-black/8 rounded-2xl p-5 hover:border-black/20 hover:shadow-sm transition-all">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-black/55 font-medium">{lbl}</div>
          <div className="font-display font-extrabold text-3xl text-black tracking-tight mt-1.5">{val}</div>
          {delta && (
            <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#1FA868]">
              <TrendingUp size={11} /> {delta}
            </div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl ${t.bg} ${t.txt} flex items-center justify-center ring-4 ring-inset ${t.ring}`}>
          <Icon size={18} />
        </div>
      </div>
    </Wrapper>
  );
}

function MiniBars({ data = [] }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex items-end gap-3 h-40 pt-3">
      {data.map((d, i) => {
        const h = Math.max(6, (d.value / max) * 140);
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-2 group">
            <div className={`text-[10px] font-semibold ${d.value === 0 ? "text-transparent" : "text-black/40"}`}>{d.value}</div>
            <div className="w-full bg-[#F4F8F3] rounded-t-md overflow-hidden flex items-end" style={{ height: 140 }}>
              <div
                className="w-full rounded-t-md bg-gradient-to-t from-[#1B4332] to-[#2D6A4F] group-hover:opacity-90 transition-opacity"
                style={{ height: h }}
              />
            </div>
            <div className="text-[11px] text-black/50 font-medium">{d.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function Donut({ segments, total }) {
  // SVG donut
  const R = 52, C = 2 * Math.PI * R;
  let offset = 0;
  const totalN = Math.max(1, total);
  return (
    <div className="flex items-center gap-6">
      <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
        <circle cx="70" cy="70" r={R} fill="none" stroke="#F4F4F4" strokeWidth="16" />
        {segments.map((s, i) => {
          const len = (s.value / totalN) * C;
          const el = (
            <circle
              key={i}
              cx="70" cy="70" r={R} fill="none"
              stroke={s.color} strokeWidth="16"
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="space-y-2 flex-1">
        {segments.map((s) => (
          <div key={s.label} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              <span className="text-black/70">{s.label}</span>
            </div>
            <span className="font-semibold text-black tabular-nums">{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [txs, setTxs] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/transactions").then((r) => setTxs(r.data.slice(0, 5))).catch(() => {});
  }, []);

  if (!stats) return <div className="p-10 text-black/50">Loading dashboard...</div>;

  const sub = stats.subscription_status || {};
  const segs = [
    { label: "Active", value: sub.active || 0, color: "#1FA868" },
    { label: "Pending", value: sub.pending || 0, color: "#F59E0B" },
    { label: "Suspended", value: sub.suspended || 0, color: "#64748B" },
    { label: "Rejected", value: sub.rejected || 0, color: "#E11D48" },
  ];
  const subTotal = segs.reduce((s, x) => s + x.value, 0);

  return (
    <div data-testid="admin-dashboard" className="p-8 lg:p-10 max-w-[1400px]">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#1FA868] font-bold">Master Control</div>
          <h1 className="font-display font-extrabold text-4xl text-black tracking-tight mt-1">Dashboard</h1>
          <p className="text-black/60 mt-1.5 text-sm">Platform overview, tenant health and revenue at a glance.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/pending-payments" data-testid="btn-pending-payments"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold text-black hover:border-black/25">
            Pending Payments <ArrowUpRight size={14} />
          </Link>
          <Link to="/admin/tenants?status_filter=pending" data-testid="btn-review-pending"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#133124]">
            Review Pending <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard lbl="Total Tenants" val={stats.total_tenants} tone="violet" icon={Building2} to="/admin/tenants" />
        <StatCard lbl="Active" val={stats.active} tone="emerald" icon={CheckCircle2} to="/admin/tenants?status_filter=active" />
        <StatCard lbl="Pending" val={stats.pending} tone="amber" icon={Clock} to="/admin/tenants?status_filter=pending" />
        <StatCard lbl="Suspended" val={stats.suspended} tone="slate" icon={Ban} to="/admin/tenants?status_filter=suspended" />
        <StatCard lbl="Rejected" val={stats.rejected} tone="rose" icon={Ban} to="/admin/tenants?status_filter=rejected" />
        <StatCard lbl="Active Plans" val={stats.active_plans} tone="sky" icon={CreditCard} to="/admin/plans" />
      </div>

      {/* Revenue panel + Tenants chart */}
      <div className="grid lg:grid-cols-3 gap-5 mb-8">
        <div className="bg-gradient-to-br from-[#0F2A1F] via-[#1B4332] to-[#2D6A4F] text-white rounded-2xl p-7 relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-48 h-48 rounded-full bg-white/5 blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-wider font-bold">
              <DollarSign size={14} /> Total MRR
            </div>
            <div className="font-display font-extrabold text-5xl mt-2 tracking-tight">
              ₦{Number(stats.mrr || 0).toLocaleString()}
            </div>
            <div className="text-white/70 text-xs mt-1">Across {stats.active} active workspaces</div>
            <div className="mt-6 pt-5 border-t border-white/15 grid grid-cols-3 gap-3 text-sm">
              <div><div className="text-white/60 text-xs">Active</div><div className="font-semibold text-lg">{stats.active}</div></div>
              <div><div className="text-white/60 text-xs">Pending</div><div className="font-semibold text-lg">{stats.pending}</div></div>
              <div><div className="text-white/60 text-xs">Suspended</div><div className="font-semibold text-lg">{stats.suspended}</div></div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-black/8 p-7">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display font-bold text-lg text-black">Tenants Created</h3>
              <p className="text-xs text-black/55">Last 6 months</p>
            </div>
            <div className="text-sm font-semibold text-[#1FA868] inline-flex items-center gap-1">
              <TrendingUp size={14} /> {stats.tenants_created_series?.reduce((a, b) => a + b.value, 0) || 0} total
            </div>
          </div>
          <MiniBars data={stats.tenants_created_series || []} />
        </div>
      </div>

      {/* Second row: Subscriptions donut + Recent tenants + Recent transactions */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-black/8 p-7">
          <h3 className="font-display font-bold text-lg text-black mb-4">Subscription Status</h3>
          <Donut segments={segs} total={subTotal} />
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-black/8 p-7">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-black">Recent Tenants</h3>
            <Link to="/admin/tenants" className="text-xs font-semibold text-[#1B4332] hover:underline">View all →</Link>
          </div>
          <div className="divide-y divide-black/8">
            {(stats.recent_tenants || []).map((t) => (
              <Link key={t.id} to={`/admin/tenants/${t.id}`}
                data-testid={`dash-recent-${t.subdomain}`}
                className="flex items-center justify-between py-3 hover:bg-[#FFFCF2] -mx-3 px-3 rounded-lg transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-[#F4F8F3] text-[#1B4332] flex items-center justify-center font-bold text-xs">
                    {(t.company_name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-black truncate text-sm">{t.company_name}</div>
                    <div className="text-[11px] text-black/50 truncate">{t.subdomain}.brivopos.com · {t.plan_name}</div>
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${
                  t.status === "active" ? "bg-[#E8F5E9] text-[#1FA868]" :
                  t.status === "pending" ? "bg-[#FFF5D6] text-amber-700" :
                  t.status === "rejected" ? "bg-rose-50 text-rose-600" : "bg-black/5 text-black/60"
                }`}>{t.status}</span>
              </Link>
            ))}
            {(stats.recent_tenants || []).length === 0 && (
              <div className="py-8 text-center text-black/50 text-sm">No tenants yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Recent transactions */}
      <div className="mt-5 bg-white rounded-2xl border border-black/8 p-7">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-black">Recent Transactions</h3>
          <Link to="/admin/transactions" className="text-xs font-semibold text-[#1B4332] hover:underline">View all →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-black/50 tracking-wider">
              <tr className="text-left">
                <th className="py-2 font-semibold">Tenant</th>
                <th className="py-2 font-semibold">Reference</th>
                <th className="py-2 font-semibold">Method</th>
                <th className="py-2 font-semibold">Amount</th>
                <th className="py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/8">
              {txs.map((t) => (
                <tr key={t.id} className="hover:bg-[#FFFCF2]">
                  <td className="py-3 font-semibold text-black">{t.tenant}</td>
                  <td className="py-3 text-black/70 font-mono text-xs">{t.reference}</td>
                  <td className="py-3 text-black/70 capitalize">{(t.method || "").replace("_", " ")}</td>
                  <td className="py-3 tabular-nums text-black">₦{Number(t.amount || 0).toLocaleString()}</td>
                  <td className="py-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${
                      t.status === "verified" ? "bg-[#E8F5E9] text-[#1FA868]" : "bg-[#FFF5D6] text-amber-700"
                    }`}>{(t.status || "").replace("_", " ")}</span>
                  </td>
                </tr>
              ))}
              {txs.length === 0 && (
                <tr><td colSpan="5" className="py-6 text-center text-black/50">No transactions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
